/**
 * Biometric Data Generator - Synthetic Wearable Data for Athletes
 *
 * Generates realistic biometric data from consumer wearables:
 * - Whoop: Strain, recovery, HRV, sleep performance
 * - Oura Ring: Readiness, sleep stages, activity, body temperature
 * - Neuphoria: Mental state, focus, neural fatigue, brainwave patterns
 * - Garmin: GPS, heart rate, VO2 max, training load
 * - Apple Watch: Activity rings, heart rate, workouts
 *
 * Randomizes which devices each athlete has active/inactive.
 */

// Device probability weights (how likely an athlete has this device)
const DEVICE_PROBABILITIES = {
  whoop: 0.75,        // Very common among pro athletes
  oura: 0.60,         // Popular for sleep tracking
  neuphoria: 0.25,    // Newer, less common (EEG neurofeedback)
  garmin: 0.45,       // Common for GPS/running athletes
  appleWatch: 0.55,   // General use
};

/**
 * Seeded random number generator for consistent player data
 */
const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

/**
 * Get a random number between min and max using player seed
 */
const getRandomInRange = (seed, min, max, decimals = 0) => {
  const value = min + seededRandom(seed) * (max - min);
  return decimals > 0 ? parseFloat(value.toFixed(decimals)) : Math.round(value);
};

/**
 * Determine if device is active for this player
 */
const isDeviceActive = (playerId, deviceName) => {
  // Use player ID as seed for consistency
  const seed = typeof playerId === 'string'
    ? playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : playerId;

  // Different seed offset for each device
  const deviceOffsets = { whoop: 1, oura: 2, neuphoria: 3, garmin: 4, appleWatch: 5 };
  const deviceSeed = seed + (deviceOffsets[deviceName] || 0) * 1000;

  return seededRandom(deviceSeed) < DEVICE_PROBABILITIES[deviceName];
};

/**
 * Generate Whoop data
 */
const generateWhoopData = (playerId) => {
  const seed = typeof playerId === 'string'
    ? playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : playerId;

  const recovery = getRandomInRange(seed + 100, 45, 98);
  const strain = getRandomInRange(seed + 101, 8, 18, 1);
  const hrv = getRandomInRange(seed + 102, 45, 95);
  const sleepPerformance = getRandomInRange(seed + 103, 65, 98);
  const sleepHours = getRandomInRange(seed + 104, 5.5, 9.0, 1);
  const restingHR = getRandomInRange(seed + 105, 42, 65);

  return {
    connected: true,
    deviceName: 'WHOOP',
    deviceIcon: 'whoop',
    color: '#00D4AA', // Whoop teal
    recovery,
    recoveryStatus: recovery >= 67 ? 'green' : recovery >= 34 ? 'yellow' : 'red',
    strain,
    strainStatus: strain >= 14 ? 'high' : strain >= 10 ? 'medium' : 'low',
    hrv,
    hrvTrend: seededRandom(seed + 106) > 0.5 ? 'up' : 'down',
    sleepPerformance,
    sleepHours,
    restingHR,
    dayStrain: getRandomInRange(seed + 107, 0, strain, 1),
    caloriesBurned: getRandomInRange(seed + 108, 2200, 4500),
  };
};

/**
 * Generate Oura Ring data
 */
const generateOuraData = (playerId) => {
  const seed = typeof playerId === 'string'
    ? playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : playerId;

  const readiness = getRandomInRange(seed + 200, 50, 98);
  const sleepScore = getRandomInRange(seed + 201, 55, 95);
  const activityScore = getRandomInRange(seed + 202, 40, 100);
  const bodyTemp = getRandomInRange(seed + 203, -0.5, 0.8, 1);
  const hrvBalance = getRandomInRange(seed + 204, 60, 100);

  return {
    connected: true,
    deviceName: 'Oura',
    deviceIcon: 'oura',
    color: '#8B5CF6', // Oura purple
    readiness,
    readinessStatus: readiness >= 70 ? 'optimal' : readiness >= 50 ? 'fair' : 'pay_attention',
    sleepScore,
    sleepStages: {
      deep: getRandomInRange(seed + 205, 45, 120), // minutes
      rem: getRandomInRange(seed + 206, 60, 140),
      light: getRandomInRange(seed + 207, 180, 300),
      awake: getRandomInRange(seed + 208, 5, 45),
    },
    activityScore,
    activityGoal: getRandomInRange(seed + 209, 300, 600), // calories target
    activityProgress: getRandomInRange(seed + 210, 60, 120), // percent of goal
    bodyTemp: bodyTemp >= 0 ? `+${bodyTemp}` : bodyTemp.toString(),
    bodyTempStatus: Math.abs(bodyTemp) <= 0.3 ? 'normal' : 'elevated',
    hrvBalance,
    restfulSleep: getRandomInRange(seed + 211, 70, 95),
    respiratoryRate: getRandomInRange(seed + 212, 12, 18, 1),
  };
};

/**
 * Generate Neuphoria data (EEG Neurofeedback)
 * Based on https://neuphoria.io/ brain optimization metrics
 */
const generateNeurophoriaData = (playerId) => {
  const seed = typeof playerId === 'string'
    ? playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : playerId;

  const mentalState = getRandomInRange(seed + 300, 55, 98);
  const focusScore = getRandomInRange(seed + 301, 50, 95);
  const flowStateReadiness = getRandomInRange(seed + 302, 40, 90);

  const neuralFatigueOptions = ['low', 'moderate', 'elevated'];
  const neuralFatigueIndex = getRandomInRange(seed + 303, 0, 2);
  const neuralFatigue = neuralFatigueOptions[neuralFatigueIndex];

  const mindsetOptions = ['focused', 'creative', 'analytical', 'relaxed'];
  const mindsetIndex = getRandomInRange(seed + 304, 0, 3);
  const mindset = mindsetOptions[mindsetIndex];

  const nervousSystemOptions = ['balanced', 'sympathetic', 'parasympathetic'];
  const nsIndex = getRandomInRange(seed + 305, 0, 2);
  const nervousSystem = nervousSystemOptions[nsIndex];

  return {
    connected: true,
    deviceName: 'Neuphoria',
    deviceIcon: 'neuphoria',
    color: '#00D9FF', // Neuphoria cyan/blue
    mentalState,
    mentalStateStatus: mentalState >= 75 ? 'peak' : mentalState >= 55 ? 'good' : 'training_needed',
    focusScore,
    flowStateReadiness,
    flowStateStatus: flowStateReadiness >= 70 ? 'ready' : 'building',
    neuralFatigue,
    neuralFatigueLevel: neuralFatigueIndex === 0 ? 'green' : neuralFatigueIndex === 1 ? 'yellow' : 'red',
    mindset,
    nervousSystem,
    brainwaveBalance: getRandomInRange(seed + 306, 60, 95),
    stressRecovery: getRandomInRange(seed + 307, 55, 95),
    cognitiveLoad: getRandomInRange(seed + 308, 20, 80),
    emotionalRegulation: getRandomInRange(seed + 309, 60, 98),
    // UMPI - Universal Mental Performance Index (Neuphoria's scoring system)
    umpiScore: getRandomInRange(seed + 310, 60, 95),
  };
};

/**
 * Generate Garmin data
 */
const generateGarminData = (playerId) => {
  const seed = typeof playerId === 'string'
    ? playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : playerId;

  const vo2Max = getRandomInRange(seed + 400, 42, 65);
  const trainingLoad = getRandomInRange(seed + 401, 200, 800);
  const bodyBattery = getRandomInRange(seed + 402, 25, 100);
  const stressLevel = getRandomInRange(seed + 403, 15, 75);

  const trainingStatusOptions = ['productive', 'maintaining', 'recovery', 'unproductive', 'peaking'];
  const tsIndex = getRandomInRange(seed + 404, 0, 4);

  return {
    connected: true,
    deviceName: 'Garmin',
    deviceIcon: 'garmin',
    color: '#0085CC', // Garmin blue
    vo2Max,
    vo2MaxTrend: seededRandom(seed + 405) > 0.4 ? 'improving' : 'stable',
    trainingLoad,
    trainingLoadStatus: trainingLoad >= 600 ? 'high' : trainingLoad >= 350 ? 'optimal' : 'low',
    trainingStatus: trainingStatusOptions[tsIndex],
    bodyBattery,
    bodyBatteryStatus: bodyBattery >= 70 ? 'high' : bodyBattery >= 40 ? 'medium' : 'low',
    stressLevel,
    stressStatus: stressLevel <= 30 ? 'low' : stressLevel <= 55 ? 'medium' : 'high',
    restingHR: getRandomInRange(seed + 406, 44, 62),
    lactateThreshold: getRandomInRange(seed + 407, 155, 185),
    steps: getRandomInRange(seed + 408, 5000, 18000),
    activeMinutes: getRandomInRange(seed + 409, 30, 180),
  };
};

/**
 * Generate Apple Watch data
 */
const generateAppleWatchData = (playerId) => {
  const seed = typeof playerId === 'string'
    ? playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : playerId;

  const moveGoal = getRandomInRange(seed + 500, 500, 900);
  const moveProgress = getRandomInRange(seed + 501, 60, 150); // percent
  const exerciseMinutes = getRandomInRange(seed + 502, 15, 90);
  const standHours = getRandomInRange(seed + 503, 6, 14);

  return {
    connected: true,
    deviceName: 'Apple Watch',
    deviceIcon: 'appleWatch',
    color: '#FF2D55', // Apple fitness pink
    moveGoal,
    moveProgress,
    moveCalories: Math.round(moveGoal * (moveProgress / 100)),
    exerciseMinutes,
    exerciseGoal: 30,
    standHours,
    standGoal: 12,
    ringsCompleted: (moveProgress >= 100 ? 1 : 0) + (exerciseMinutes >= 30 ? 1 : 0) + (standHours >= 12 ? 1 : 0),
    heartRate: getRandomInRange(seed + 504, 55, 80),
    heartRateVariability: getRandomInRange(seed + 505, 35, 85),
    walkingAsymmetry: getRandomInRange(seed + 506, 0, 15, 1),
    cardioFitness: getRandomInRange(seed + 507, 38, 58, 1),
    sleepHours: getRandomInRange(seed + 508, 5.5, 8.5, 1),
  };
};

/**
 * Main function: Generate complete biometric profile for a player
 */
export const generateBiometricProfile = (playerId, playerName = '') => {
  // Use player ID for consistent seeding
  const seed = typeof playerId === 'string'
    ? playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : playerId;

  const devices = {};
  let activeDeviceCount = 0;
  let primaryReadiness = null;

  // Check each device and generate data if active
  if (isDeviceActive(playerId, 'whoop')) {
    devices.whoop = generateWhoopData(playerId);
    activeDeviceCount++;
    if (!primaryReadiness) primaryReadiness = devices.whoop.recovery;
  } else {
    devices.whoop = { connected: false, deviceName: 'WHOOP', deviceIcon: 'whoop', color: '#00D4AA' };
  }

  if (isDeviceActive(playerId, 'oura')) {
    devices.oura = generateOuraData(playerId);
    activeDeviceCount++;
    if (!primaryReadiness) primaryReadiness = devices.oura.readiness;
  } else {
    devices.oura = { connected: false, deviceName: 'Oura', deviceIcon: 'oura', color: '#8B5CF6' };
  }

  if (isDeviceActive(playerId, 'neuphoria')) {
    devices.neuphoria = generateNeurophoriaData(playerId);
    activeDeviceCount++;
  } else {
    devices.neuphoria = { connected: false, deviceName: 'Neuphoria', deviceIcon: 'neuphoria', color: '#00D9FF' };
  }

  if (isDeviceActive(playerId, 'garmin')) {
    devices.garmin = generateGarminData(playerId);
    activeDeviceCount++;
  } else {
    devices.garmin = { connected: false, deviceName: 'Garmin', deviceIcon: 'garmin', color: '#0085CC' };
  }

  if (isDeviceActive(playerId, 'appleWatch')) {
    devices.appleWatch = generateAppleWatchData(playerId);
    activeDeviceCount++;
  } else {
    devices.appleWatch = { connected: false, deviceName: 'Apple Watch', deviceIcon: 'appleWatch', color: '#FF2D55' };
  }

  // Calculate composite scores from available devices
  const compositeReadiness = calculateCompositeReadiness(devices);
  const overallStatus = getOverallStatus(compositeReadiness);

  return {
    playerId,
    playerName,
    devices,
    activeDeviceCount,
    totalDevices: 5,
    hasAnyDevice: activeDeviceCount > 0,
    compositeReadiness,
    overallStatus,
    lastSync: generateLastSyncTime(seed),
  };
};

/**
 * Calculate composite readiness from all available device data
 */
const calculateCompositeReadiness = (devices) => {
  const scores = [];

  if (devices.whoop?.connected) scores.push(devices.whoop.recovery);
  if (devices.oura?.connected) scores.push(devices.oura.readiness);
  if (devices.neuphoria?.connected) scores.push(devices.neuphoria.mentalState);
  if (devices.garmin?.connected) scores.push(devices.garmin.bodyBattery);

  if (scores.length === 0) return null;

  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

/**
 * Get overall status based on composite readiness
 */
const getOverallStatus = (readiness) => {
  if (readiness === null) return 'no_data';
  if (readiness >= 85) return 'excellent';
  if (readiness >= 70) return 'good';
  if (readiness >= 55) return 'moderate';
  return 'needs_attention';
};

/**
 * Generate a realistic "last sync" time
 */
const generateLastSyncTime = (seed) => {
  const minutesAgo = getRandomInRange(seed + 900, 5, 180);

  if (minutesAgo < 60) {
    return `${minutesAgo}m ago`;
  } else {
    return `${Math.floor(minutesAgo / 60)}h ago`;
  }
};

/**
 * Get device icon component props
 */
export const getDeviceIconProps = (deviceIcon) => {
  const iconMap = {
    whoop: { emoji: '🔋', label: 'Whoop' },
    oura: { emoji: '💍', label: 'Oura' },
    neuphoria: { emoji: '🧠', label: 'Neuphoria' },
    garmin: { emoji: '⌚', label: 'Garmin' },
    appleWatch: { emoji: '⌚', label: 'Apple Watch' },
  };
  return iconMap[deviceIcon] || { emoji: '📱', label: 'Device' };
};

/**
 * Get status color for display
 */
export const getStatusColor = (status) => {
  const colorMap = {
    excellent: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    good: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    moderate: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
    needs_attention: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
    no_data: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
  };
  return colorMap[status] || colorMap.no_data;
};

export default {
  generateBiometricProfile,
  getDeviceIconProps,
  getStatusColor,
  isDeviceActive,
};
